/**
 * @file ProfileDropdown.tsx
 * @description Composant React représentant un menu déroulant de profil utilisateur avec un avatar, des options de menu et une fonctionnalité de déconnexion.
 */

import React, { useEffect, useState } from 'react';
import { Avatar, Dropdown, Menu, Button, Layout } from 'antd';
import { UserOutlined, LogoutOutlined, SyncOutlined } from '@ant-design/icons';
import { Link, useNavigate } from 'react-router-dom'; // Importation de Link pour la navigation
import './AppHeader.css';
import { isStagOrDev } from '../../utils/env';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../../store/auth/useAuthStore';
import { useBalanceStore } from '../../store/balance/useBalanceStore';
import { useTagsStore } from '../../store/tags/useTagsStore';
import { useExercicesStore } from '../../store/exercices/useExercicesStore';
import { useCutOffStore } from '../../store/cutOff/useCutOffStore';
import { useTransactionsStore } from '../../store/transactions/useTransactionsStore';
import { usePiecesStore } from '../../store/pieces/usePiecesStore';
import { usePointsSuspensStore } from '../../store/pointsSuspens/usePointsSuspensStore';
import { useCabinetStore } from '../../store/cabinet/useCabinetStore';
import { useCompaniesStore } from '../../store/companies/useCompaniesStore';
import { useQuestionnaireStore } from '../../store/questionnaire/useQuestionnaireStore';
import { useEmpruntsStore } from '../../store/emprunt/useEmpruntsStore';
import { useFinconnectStore } from 'src/store/finconnect/useFinconnectStore';

const { Header } = Layout;

/**
 * @component ProfileDropdown
 * @description Affiche un menu déroulant contenant les options de profil pour l'utilisateur connecté, comme la déconnexion.
 * @returns {JSX.Element} Le menu déroulant de profil avec un avatar et des options de menu.
 */
const AppHeader: React.FC = () => {
  const user = useAuthStore((state) => state.user); // Récupération de l'utilisateur depuis le store

  // État pour stocker les initiales de l'utilisateur
  const [initials, setInitials] = useState<string>('');
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // Initialisation des initiales de l'utilisateur en fonction de son e-mail
  useEffect(() => {
    if (user?.email) {
      const nameParts = user.email.split('.');
      const initials = nameParts
        .slice(0, -1) // Ignorer la partie domaine de l'email
        .map((part) => part[0]) // Récupérer la première lettre de chaque partie
        .join('');
      setInitials(initials.toUpperCase()); // Mettre en majuscules les initiales
    }
  }, [user?.email]);
  /**
   * @method logout
   * @description Fonction déclenchée lorsque l'utilisateur clique sur "Se déconnecter".
   */

  // Menu du dropdown, contient les placeholders et l'option de déconnexion
  const menu = (
    <Menu>
      <Menu.Item key="1">
        <Button type="text">Profile</Button>
      </Menu.Item>
      <Menu.Item key="2" onClick={() => handleDeleteCacheAndLogOut()}>
        <span>
          <SyncOutlined />
          Supprimer le cache
        </span>
      </Menu.Item>
      <Menu.Item
        key="3"
        onClick={() => useAuthStore.getState().logout(navigate)}
        data-testid="logout"
      >
        <LogoutOutlined />
        Se déconnecter
      </Menu.Item>
    </Menu>
  );
  const handleDeleteCacheAndLogOut = () => {
    queryClient.clear();
    useBalanceStore.getState().reset();
    useTagsStore.getState().reset();
    useExercicesStore.getState().reset();
    useCutOffStore.getState().reset();
    useTransactionsStore.getState().reset();
    usePiecesStore.getState().reset();
    usePointsSuspensStore.getState().reset();
    useCabinetStore.getState().reset();
    useCompaniesStore.getState().reset();
    useQuestionnaireStore.getState().reset();
    useEmpruntsStore.getState().reset();
    useAuthStore.getState().logout(navigate);
    useFinconnectStore.getState().reset();
  };
  return (
    <>
      {/* Nom de l'application cliquable si l'utilisateur est connecté */}
      {user ? (
        <Header className="app-header">
          <Link to="/cabinet" className="app-name-link">
            <div className="app-name">COMPTANOVA</div>
          </Link>
          <div className="user-info">
            <Dropdown overlay={menu} placement="bottomRight" data-testid="dropDownMenu">
              <Avatar
                className="profile-avatar"
                style={{ backgroundColor: '#007bff' }}
                data-testid="avatar"
              >
                {initials || <UserOutlined />}{' '}
                {/* Affichage des initiales ou d'une icône si non disponible */}
              </Avatar>
            </Dropdown>
          </div>
        </Header>
      ) : (
        <></>
      )}
    </>
  );
};

export default AppHeader;
